/**
 * 운영 도구 - 서비스 선택 메뉴
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import zoolungLogo from '../assets/service-menu/zoolung-logo.svg';
import api, { checkUsbBridge, checkBridgeUpdate, triggerBridgeUpdate } from '../services/api';
import { isAtLeast } from '../utils/roleUtils';
import { isBridgeApp } from '../utils/isBridgeApp';
import bankIcon from '../assets/service-menu/bank-menu.png';
import couponRedeemIcon from '../assets/service-menu/coupon-redeem.png';
import couponToolsIcon from '../assets/service-menu/coupon-tools.png';
import passportCancelIcon from '../assets/service-menu/passport-cancel.png';
import questSpotIcon from '../assets/service-menu/quest-spot.png';
import missionManualIcon from '../assets/service-menu/mission-manual.png';
import zookeeperHqIcon from '../assets/service-menu/zookeeper-hq.png';
import zoolungmanIcon from '../assets/service-menu/zoolungman.png';
import developerCubeIcon from '../assets/service-menu/developer-cube.png';
import './ServiceMenuPage.css';
import LauncherServiceCard from '../components/LauncherServiceCard';
import useMenuFavorites from '../hooks/useMenuFavorites';
import useLauncherCopyFit from '../hooks/useLauncherCopyFit';
import { MAX_MENU_FAVORITES } from '../utils/menuFavorites';

// 현재 현장 운영에서 사용하지 않는 회원 알림·QR 출력 카드는 기능/라우트를 유지한 채 숨긴다.
const SHOW_DEFERRED_MEMBER_SERVICE_CARDS = false;

/**
 * 서비스 카드 정의.
 * `minRole` 은 **선택** 필드입니다 — 없으면 모든 로그인 사용자에게 보입니다(기존 카드 전부 해당).
 * 값이 있으면 `visibleServices` 가 해당 역할 미만 사용자에게서 카드를 감춥니다.
 * `hidden` 이 true 인 카드는 현장 메뉴에서만 숨기며 라우트와 기능 코드는 유지합니다.
 * (테스트가 실제 배열을 검증하므로 `export` — `utils/roleUtils.test.js`)
 */
export const SERVICES = [
  {
    id: 'all-in-one',
    path: '/all',
    icon: '🧩',
    iconImage: bankIcon,
    title: '주렁은행',
    description: '주렁주화 저축 · 여권 관리',
    color: '#455A64',
  },
  {
    id: 'alimtalk',
    path: '/alimtalk',
    icon: '💬',
    title: '알림톡',
    description: '멤버십 가입 알림톡 발송',
    hidden: !SHOW_DEFERRED_MEMBER_SERVICE_CARDS,
    color: '#1E88E5',
  },
  // 가족 정보는 멤버조회에 통합됨 (라우트/코드 유지)
  {
    id: 'qr-sticker',
    path: '/qr-sticker',
    icon: '🖨️',
    title: 'QR 스티커',
    description: '리더기/카메라/전화번호 출력',
    hidden: !SHOW_DEFERRED_MEMBER_SERVICE_CARDS,
    color: '#00897B',
  },
  {
    id: 'qr-auto',
    path: '/qr-auto',
    icon: '⚡',
    title: 'QR스티커(자동)',
    description: 'QR리더 스캔 → 즉시 출력',
    hidden: !SHOW_DEFERRED_MEMBER_SERVICE_CARDS,
    color: '#FF6F00',
  },
  {
    id: 'coupon-redeem',
    path: '/coupon-redeem',
    icon: '🎟️',
    iconImage: couponRedeemIcon,
    title: '쿠폰 사용/취소',
    description: '쿠폰 사용·취소 처리',
    color: '#0EA5E9',
  },
  {
    id: 'pos-coupon-ops',
    path: '/pos-coupon-ops',
    icon: '🖥️',
    iconImage: couponToolsIcon,
    title: '쿠폰도구',
    description: '쿠폰 전체 조회 및 사용/발급',
    color: '#0284C7',
  },
  {
    id: 'passport-cancel',
    path: '/passport-cancel',
    icon: '🛂',
    iconImage: passportCancelIcon,
    title: '여권 취소',
    description: '구매 여권 취소 후 혜택 회수 안내',
    // 🔴 서버 `/api/v1/tools/passport/by-qr/*` 2종이 `require_role("STAFF")` 입니다(D5 — 취소가
    //    주화를 차감하므로). PART_TIME 에게 카드를 보여주면 스캔까지 하고 취소에서만
    //    403 이 나는 dead-end 가 됩니다. 라우트(App.js)에도 같은 minRole 을 겁니다.
    minRole: 'STAFF',
    color: '#B45309',
  },
  {
    id: 'quest-tag',
    testOnly: true,
    path: '/quest-tag',
    icon: '🌿',
    iconImage: questSpotIcon,
    title: '퀘스트 스팟',
    description: '각 장소에 맞는 미션 재생',
    color: '#10B981',
  },
  {
    id: 'quest-manual-check',
    testOnly: true,
    path: '/quest-manual-check',
    icon: '✅',
    iconImage: missionManualIcon,
    title: '미션 수동확인',
    description: '직원 확인 후 회원 QR로 미션 완료',
    color: '#059669',
  },
  {
    id: 'quest-status',
    testOnly: true,
    path: '/quest-status',
    icon: '🎉',
    iconImage: zookeeperHqIcon,
    title: '주키퍼 본부',
    description: '퀘스트 현황 확인 · 완료 연출',
    color: '#22C55E',
  },
  {
    id: 'zoolungman',
    testOnly: true,
    path: '/zoolungman',
    icon: '🌳',
    iconImage: zoolungmanIcon,
    title: '주렁맨',
    description: '미션 진행 후 주화 지급',
    // 🔴 서버 `POST /api/v1/tools/coin/give` 가 `require_role("STAFF")` 이므로(최종 방어),
    //    PART_TIME 에게 카드를 보여주면 스캔까지 하고 지급에서만 403 이 나는 dead-end 가 됩니다.
    minRole: 'STAFF',
    color: '#4D7C0F',
  },
];

// 밴드QR 출력 카드 노출 여부 — 테스트 서버에서도 숨긴다(2026-08-06 사용자 요청).
// 위 SHOW_DEFERRED_MEMBER_SERVICE_CARDS 와 같은 방식이다: 정의를 지우지 않고 플래그로만
// 가려 두어 필요해지면 이 한 줄만 true 로 되돌리면 된다.
const SHOW_BAND_QR_PRINT_CARD = false;

export const TEST_SERVICES = [
  {
    id: 'virtual-kiosk',
    path: '/virtual-kiosk',
    icon: '🖥️',
    title: '가상 키오스크',
    description: '킨더랩스 API 시뮬레이션',
    devOnly: true,
    color: '#1565C0',
  },
  {
    id: 'band-qr-print',
    path: '/band-qr-print',
    icon: '🏷️',
    title: '밴드QR 출력',
    description: '랜덤 밴드QR을 매수만큼 인쇄',
    color: '#EA580C',
    // 카드만 숨긴다 (2026-08-06 사용자 요청) — 라우트·페이지는 그대로라
    // 위 상수만 true 로 되돌리면 즉시 복구된다. 주소로 직접 접근하는 것도 계속 가능하다.
    hidden: !SHOW_BAND_QR_PRINT_CARD,
  },
  {
    id: 'passport-test',
    path: '/passport-test',
    icon: '🧪',
    title: '여권 테스트',
    description: '여권QR 발급·부여 (TEST 전용)',
    devOnly: true,
    // 🔴 서버 `/api/v1/tools/passport-test/*` 3종이 `require_role("STAFF")` 입니다.
    //    PART_TIME 에게 카드를 보여주면 들어가서 발급에서만 403 이 나는 dead-end 가 됩니다.
    //    라우트(App.js)에도 같은 minRole 을 겁니다.
    // ⚠️ TEST_SERVICES 에서 `minRole` 을 쓰는 첫 카드입니다. 필터(`visibleServices`)는
    //    SERVICES 와 같은 한 줄을 그대로 타므로 노출 로직 변경은 없습니다.
    minRole: 'STAFF',
    color: '#7C3AED',
  },
  {
    id: 'quest-completion-test',
    path: '/quest-completion-test',
    icon: '☑️',
    title: '미션완료 테스트',
    description: 'QR 회원 미션 완료상태 편집',
    devOnly: true,
    // 임의 상태 편집 API가 STAFF + TEST 서버 전용이므로 메뉴와 라우트도 같은 등급으로 맞춘다.
    minRole: 'STAFF',
    color: '#7C3AED',
  },
];

/**
 * 화면에 그릴 카드 목록 — 테스트모드 합성 + **역할 게이팅**(dev-steps §S5 작업 4).
 *
 * `testOnly` 카드는 서버가 TEST임을 확인한 경우에만 노출합니다.
 * 조회 대기/실패 시 기본 false를 유지하며, 역할 필터는 환경 필터 뒤에 그대로 적용합니다.
 * `devOnly`(개발 도구 표시)와 구분하여 미션 메뉴를 개발 도구로 재분류하지 않습니다.
 * ⚠️ 이 게이팅은 UX(dead-end 방지)일 뿐 보안 경계가 아닙니다 — 최종 방어는 서버 `require_role`.
 *
 * (렌더 안 인라인이 아니라 모듈 스코프 함수로 둔 이유: `tools` 에 `@testing-library/react` 가
 *  없어 렌더 테스트가 불가능하므로, 실제 프로덕션 필터를 그대로 단위테스트하기 위함입니다.)
 *
 * @param {*} role 현재 사용자 역할(`user?.role` — 미로그인/응답누락 시 undefined 가능)
 * @param {boolean} isTestMode 서버 `/tools/server-mode` 가 `TEST` 인지
 * @returns {Array<object>} 노출할 카드 배열
 */
export const visibleServices = (role, isTestMode) =>
  [...SERVICES, ...(isTestMode === true ? TEST_SERVICES : [])]
    .filter((s) => !s.hidden)
    .filter((s) => !s.testOnly || isTestMode === true)
    .filter((s) => !s.minRole || isAtLeast(role, s.minRole));

export const splitMenuServices = (services) => ({
  operational: services.filter((service) => !service.devOnly),
  developer: services.filter((service) => service.devOnly),
});

const MENU_CATEGORIES = [
  { id: 'member', title: '주화/쿠폰/여권 관리', ids: ['all-in-one', 'coupon-redeem', 'pos-coupon-ops', 'passport-cancel'] },
  { id: 'mission', title: '미션 관리', ids: ['quest-tag', 'quest-manual-check', 'quest-status', 'zoolungman'] },
];
const SERVICE_SUMMARIES = {
  'all-in-one': '주화 저축과 여권 관리',
  'coupon-redeem': '쿠폰 사용 처리와 사용 취소',
  'quest-manual-check': '직원 확인 후 미션 완료',
  'quest-status': '퀘스트 진행 현황과 완료 연출',
};

const MENU_DESCRIPTION_SAMPLES = SERVICES
  .filter(service => !service.devOnly && !service.hidden)
  .map(service => SERVICE_SUMMARIES[service.id] || service.description);

export default function ServiceMenuPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isTestMode, setIsTestMode] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null); // null | 'checking' | 'available' | 'latest' | 'updating' | 'error'
  const [updateInfo, setUpdateInfo] = useState(null);
  const [developerMenuOpen, setDeveloperMenuOpen] = useState(false);
  const [categoryId, setCategoryId] = useState('member');
  const [openingId, setOpeningId] = useState(null);
  const [failedId, setFailedId] = useState(null);
  const openingRef = useRef(false);
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);
  const developerMenuRef = useRef(null);
  const categoryButtonsRef = useRef(null);
  useLayoutEffect(() => {
    const bar = categoryButtonsRef.current;
    if (!bar) return undefined;
    const update = () => {
      const selected = bar.querySelector('button[aria-pressed="true"]');
      bar.dataset.hasSelection = String(Boolean(selected));
      if (!selected) return;
      bar.style.setProperty('--selection-x', `${selected.offsetLeft}px`);
      bar.style.setProperty('--selection-y', `${selected.offsetTop}px`);
      bar.style.setProperty('--selection-width', `${selected.offsetWidth}px`);
      bar.style.setProperty('--selection-height', `${selected.offsetHeight}px`);
    };
    update();
    const frame = requestAnimationFrame(() => { bar.dataset.selectionReady = 'true'; });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(bar);
    bar.querySelectorAll('button').forEach(button => observer?.observe(button));
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [categoryId, isTestMode, user?.role]);
  const [favoriteLimitId, setFavoriteLimitId] = useState(null);
  const favoriteLimitTimerRef = useRef(null);
  useEffect(() => {
    setFavoriteLimitId(null);
    return () => clearTimeout(favoriteLimitTimerRef.current);
  }, [categoryId]);
  const scrollIdleTimerRef = useRef(null);

  // Paint only: never toggle overflow/scrollbar height, which would shift cards.
  const revealScrollIndicator = (event) => {
    const list = event.currentTarget;
    clearTimeout(scrollIdleTimerRef.current);
    list.dataset.scrolling = 'true';
    scrollIdleTimerRef.current = setTimeout(() => {
      delete list.dataset.scrolling;
    }, 900);
  };
  useEffect(() => () => clearTimeout(scrollIdleTimerRef.current), [categoryId]);

  useEffect(() => {
    api.get('/tools/server-mode').then(res => {
      setIsTestMode(res.mode === 'TEST');
    }).catch(() => {});
    checkUsbBridge().then(res => {
      if (res.app_version) setAppVersion(res.app_version);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!developerMenuOpen) return undefined;

    const closeOnOutside = (event) => {
      if (!developerMenuRef.current?.contains(event.target)) setDeveloperMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setDeveloperMenuOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutside, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [developerMenuOpen]);

  const handleCheckUpdate = async () => {
    setUpdateStatus('checking');
    const res = await checkBridgeUpdate();
    if (!res || !res.success) {
      setUpdateStatus('error');
      return;
    }
    setUpdateInfo(res);
    setUpdateStatus(res.has_update ? 'available' : 'latest');
  };

  const handleUpdate = async () => {
    if (!window.confirm(
      `v${updateInfo.current_version} → v${updateInfo.server_version} 업데이트를 시작합니다.\n앱이 자동으로 재시작됩니다.`
    )) return;

    setUpdateStatus('updating');
    const res = await triggerBridgeUpdate();
    if (!res.success) {
      setUpdateStatus('error');
      alert(res.message);
    }
  };

  const handleHeaderTap = () => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 6) {
      tapCountRef.current = 0;
      handleLogout();
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('store_id');
    localStorage.removeItem('store_name');
    const apiBase = process.env.REACT_APP_API_URL || 'http://192.168.0.113:59000/api/v1';
    window.location.href = `${apiBase}/auth/force-logout`;
  };

  const handleLogoutClick = async () => {
    if (!window.confirm('로그아웃하시겠습니까?\n(브라우저 인증 정보가 제거됩니다)')) return;
    try {
      await logout();
    } catch (e) {
      // 서버 호출 실패해도 로컬 상태는 정리하고 계속 진행
    }
    localStorage.removeItem('store_id');
    localStorage.removeItem('store_name');
    navigate('/login', { replace: true });
  };

  const renderUpdateButton = () => {
    if (!appVersion) return null;

    switch (updateStatus) {
      case 'checking':
        return <span className="menu-update-btn menu-update-checking">확인 중...</span>;
      case 'available':
        return (
          <span className="menu-update-btn menu-update-available" onClick={handleUpdate}>
            v{updateInfo.server_version} 업데이트
          </span>
        );
      case 'latest':
        return <span className="menu-update-btn menu-update-latest" onClick={handleCheckUpdate}>최신</span>;
      case 'updating':
        return <span className="menu-update-btn menu-update-checking">업데이트 중...</span>;
      case 'error':
        return <span className="menu-update-btn menu-update-error" onClick={handleCheckUpdate}>재시도</span>;
      default:
        return <span className="menu-update-btn" onClick={handleCheckUpdate}>업데이트 확인</span>;
    }
  };

  const availableServices = visibleServices(user?.role, isTestMode);
  const { operational: operationalServices, developer: developerServices } = splitMenuServices(availableServices);
  let storeName = null;
  let storeId = user?.store_id;
  try {
    storeName = localStorage.getItem('store_name');
    storeId = localStorage.getItem('store_id') ?? storeId;
  } catch {
    // 브라우저 저장소가 막혀도 메뉴를 사용할 수 있다.
  }
  const { favorites, toggle: toggleFavorite, notice, canSave } = useMenuFavorites(user, storeId, operationalServices);
  const handleFavoriteClick = (id) => {
    clearTimeout(favoriteLimitTimerRef.current);
    if (!favorites.some(item => item.id === id) && favorites.length >= MAX_MENU_FAVORITES) {
      setFavoriteLimitId(id);
      favoriteLimitTimerRef.current = setTimeout(() => setFavoriteLimitId(null), 2000);
      return;
    }
    setFavoriteLimitId(null);
    toggleFavorite(id);
  };
  // 홈은 화면 이동만 담당한다. 목적지의 API 오류는 해당 업무 화면이 처리한다.
  const openService = async (service) => {
    if (openingRef.current) return;
    openingRef.current = true;
    setOpeningId(service.id);
    setFailedId(null);
    try {
      if (service.external) window.location.href = service.external;
      else await navigate(service.path);
    } catch {
      setFailedId(service.id);
    } finally {
      openingRef.current = false;
      setOpeningId(null);
    }
  };
  const categories = [{ id: 'favorites', title: '즐겨찾기', ids: [] }, ...MENU_CATEGORIES.filter((category) => operationalServices.some((service) => category.ids.includes(service.id)))];
  const currentCategory = categories.find((category) => category.id === categoryId) || categories[0];
  const categoryServices = currentCategory?.id === 'favorites' ? favorites : operationalServices.filter((service) => currentCategory?.ids.includes(service.id));
  const launcherAppsRef = useRef(null);
  useLauncherCopyFit(launcherAppsRef, categoryServices.map(service => service.id).join('|'), MENU_DESCRIPTION_SAMPLES);
  const renderMenuItem = service => (
    <LauncherServiceCard key={service.id} service={service}
      description={SERVICE_SUMMARIES[service.id] || service.description}
      openingId={openingId} failedId={failedId}
      isFavorite={favorites.some(item => item.id === service.id)} canSave={canSave}
      favoriteLimitId={favoriteLimitId} onOpen={openService} onFavorite={handleFavoriteClick} />
  );


  return (
    <div className="service-menu-page launcher-page">
      <div className="desk-workspace">
      <header className="menu-header">
        <div className="menu-header-left">
          <img className="desk-logo" src={zoolungLogo} alt="주렁주렁" />
          <h1>운영 도구</h1>
        </div>
        <div className="menu-header-right">
          {storeName && <span className="desk-store"><span className="desk-store-light" aria-hidden="true" />{storeName}</span>}
          <button type="button" className="menu-user-name" onClick={handleHeaderTap}>
            <span className="desk-account-copy"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
          </button>
          {appVersion && <span className="menu-app-version">v{appVersion}</span>}
          {renderUpdateButton()}
          {/* 키오스크 앱(ZlzlKiosk WebView)에서는 로그아웃 버튼 숨김 — 한 번 로그인 후 상시 사용 */}
          {!isBridgeApp() && (
            <button className="menu-logout-btn" onClick={handleLogoutClick}>로그아웃</button>
          )}
        </div>
      </header>

      <div className="launcher-category-body">
        <nav className="launcher-category-nav" aria-label="업무 카테고리">
          <div className="launcher-category-buttons" ref={categoryButtonsRef}>
            <span className="launcher-category-selection" aria-hidden="true" />
            {categories.filter(category => category.id !== 'favorites').map((category) => <button type="button" key={category.id} data-category={category.id} aria-pressed={currentCategory?.id === category.id} aria-controls="launcher-category-panel" onClick={() => setCategoryId(category.id)}>
              <span>{category.title}</span><span aria-hidden="true">›</span>
            </button>)}
          </div>
          <button type="button" className="launcher-favorites-tab" data-category="favorites" aria-pressed={currentCategory?.id === 'favorites'} aria-controls="launcher-category-panel" onClick={() => setCategoryId('favorites')}>
            <span aria-hidden="true">☆</span> 즐겨찾기
          </button>
        </nav>
        <main id="launcher-category-panel" className="launcher-library" aria-label={currentCategory?.title || '운영 도구'}>
          <div key={currentCategory?.id} className="launcher-apps" ref={launcherAppsRef} data-empty={categoryServices.length === 0} onScroll={revealScrollIndicator} onPointerDown={revealScrollIndicator} aria-label="실행할 기능 목록">{categoryServices.map(renderMenuItem)}
            {currentCategory?.id === 'favorites' && !favorites.length && <div className="launcher-empty">카드의 별을 눌러 자주 쓰는 기능을 추가하세요.<br />최대 4개까지 모아 볼 수 있습니다.</div>}
          </div>
        </main>
      </div>
      {isTestMode && developerServices.length > 0 && (
        <div className="developer-launcher" ref={developerMenuRef}>
          {developerMenuOpen && (
            <div className="developer-menu" role="menu" aria-label="개발 기능">
              {developerServices.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  className="developer-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setDeveloperMenuOpen(false);
                    navigate(service.path);
                  }}
                >
                  <span>{service.title}</span>
                </button>
              ))}
            </div>
          )}
          <button type="button" className="developer-launcher-button" aria-label="개발 기능 열기" aria-expanded={developerMenuOpen} onClick={() => setDeveloperMenuOpen((open) => !open)}>
            <span className="developer-launcher-label">개발기능</span>
            <img className="developer-launcher-cube" src={developerCubeIcon} alt="" aria-hidden="true" />
          </button>
        </div>
      )}
      {(notice || failedId) && <div className="launcher-status" role="status">{notice || '화면 이동에 실패했습니다. 해당 기능을 다시 눌러 주세요.'}</div>}
      </div>

    </div>
  );
}
